/**
 * Typed payloads for the S0-relevant `cmd.*` / `evt.*` / `sys.*` messages, matching the catalog
 * (core-architecture §5-6) and the on-the-wire camelCase JSON produced by the S0 backend.
 *
 * Only the messages the S0 backend actually emits/accepts are typed here; gameplay messages
 * (`cmd.game.*`, `stream.*`, etc.) are deferred until the backend implements them.
 */
import type { ApiError } from './errors.ts';
import type { PresenceState } from './envelope.ts';

// ---- sys.* control plane ------------------------------------------------------------------------

/** Client -> server first frame (protocol.md §4.2). */
export interface HelloPayload {
  token: string;
  protocolVersion: number;
  clientVersion: string;
  /** One of macos|windows|linux|web (protocol.md §4.2). */
  platform: string;
  /** Client kind; the backend reads this for the forced-update gate. */
  clientType: 'web';
  language: string;
  /** Optional resume plan: per-session last applied seq. */
  resume?: ResumeEntry[];
}

export interface ResumeEntry {
  session: string;
  lastSeq: number;
}

/** Server -> client handshake confirmation (protocol.md §4.3). */
export interface WelcomePayload {
  playerId: string;
  serverTime: string;
  flags: Record<string, boolean>;
  sessions: WelcomeSession[];
}

export interface WelcomeSession {
  session: string;
  resumeFrom: number;
  /** Full state snapshot when the replay window was exceeded, else null. */
  snapshot: unknown | null;
}

/** Server -> client: a resumed session is now live (protocol.md §4.4). */
export interface LivePayload {
  session: string;
  seq: number;
}

/** Server -> client acknowledgement of a command (protocol.md §6). */
export interface AckPayload {
  cmdId: string;
  status: 'accepted' | 'rejected';
  /** First committed seq, present only when accepted. */
  seq?: number;
  /** Canonical error, present only when rejected. */
  error?: ApiError;
}

/** Client -> server reauth before token expiry (protocol.md §4.6). */
export interface ReauthPayload {
  token: string;
}

/** Server -> client rolling-deploy drain notice (protocol.md §4.7). */
export interface DrainPayload {
  graceMs: number;
}

/**
 * Server -> client operator notice / update hint (protocol.md §7), and - since playtest one - the
 * targeted refusal: one sentence for ONE player about something the server did not do for them.
 *
 * `message` is the server's own sentence and always names an action, so a client that knows nothing
 * about `code` still shows something useful. `code` is the hook for a client that can name the move in
 * its own vocabulary instead (a seat is `/claim`ed in the terminal and clicked in the workspace).
 */
export interface NoticePayload {
  message?: string;
  level?: string;
  /** A closed token; see {@link NoticeCodes}. */
  code?: string;
  /** Seat keys the client may name in its action, when the code carries any. */
  seats?: string[];
  /** How long until a retry can succeed, when a limiter is holding the door. */
  retryAfterMs?: number | null;
  [k: string]: unknown;
}

/** The notice codes the server sends today (mirror of Sessions/Npc/MentionNotices.cs). */
export const NoticeCodes = {
  MentionNoSeat: 'mention.no-seat',
  MentionUnknownSeat: 'mention.unknown-seat',
  MentionRateLimited: 'mention.rate-limited',
  /** DEC-19: the joiner was handed a seat. `seats` carries the one they got. */
  JoinSeated: 'join.seated',
  /** DEC-19: every seat was held by a person, so they landed as an observer. */
  JoinObserver: 'join.observer',
} as const;

// ---- cmd.* client intents -----------------------------------------------------------------------

export interface ChatSendPayload {
  text: string;
}

/**
 * `cmd.game.action` payload (the S1 engine bridge): a tagged gameplay input the backend maps to an
 * EngineInput (e.g. `{ kind: 'startSession' }`, `{ kind: 'moodPulse', seat, mood }`). Open shape by design.
 */
export interface GameActionPayload {
  kind: string;
  [field: string]: unknown;
}

// ---- stream.* ephemeral state (server -> clients, unsequenced) -----------------------------------

/** One seat on the shared board (from `stream.state.delta`). */
export interface SeatView {
  key: string;
  occupant: string;
  /**
   * The role's own authored name, in the reader's language (DEC-16). Null for a deck that never named
   * its roles, in which case the key is the honest fallback - the client does not invent a title.
   */
  title?: string | null;
  /**
   * Why a table wants this role filled - the scenario's `winContribution`, in the reader's language.
   * The pitch text for the "who are you?" picker; null when the deck did not write one.
   */
  pitch?: string | null;
  /**
   * The deck's own recommendation for a first-timer: true where `npcEligible` is false, i.e. the seat
   * the scenario refuses to let a bot hold. Not a rule - a picker ordering hint.
   */
  recommended?: boolean;
  /**
   * The human occupant's player id, or null (empty / NPC). The WHOLE id: it used to be shortened
   * server-side, and eight characters of a uuidv7 is a timestamp prefix two players can share.
   * Shortening for display is the client's job.
   */
  player: string | null;
  /** True when the recipient occupies this seat. */
  mine: boolean;
  checkedIn: boolean;
  /** True when no human holds it — an NPC-filled seat is claimable too (bot-fill is a floor, not a booking). */
  claimable: boolean;
  /** Whether a bot covers this seat when nobody takes it. */
  npcEligible: boolean;
  /** The verbs this seat's role carries — what you are choosing when you choose the seat. */
  powers: string[];
  /**
   * The authored character who holds this role, in the reader's language, or null when the scenario
   * wrote none. It is a NAME, not a label: the workspace client draws seats as colleagues
   * (workspace-client.md §1b), and the terminal says who a bot seat is playing rather than "(a bot)".
   * Optional on the type only so a payload recorded before this field still parses.
   */
  character?: string | null;
}

/** The running scene's guided flow (from `stream.state.delta`). */
export interface SceneView {
  kind: string;
  beat: string;
  beatIndex: number;
  beatCount: number;
  beats: string[];
  /** Seats that have used their reveals share slot. */
  shared: string[];
  /**
   * True when the scene CALENDAR opened this and nobody has engaged with it yet (first-run-review §A3).
   * An offered meeting is an invitation: it is on the board, it constrains nothing, and the first `/beat`
   * takes it. Absent on payloads recorded before A3.
   */
  offered?: boolean;
}

/**
 * The standup digest (game-loop §6.5 beat 1): what happened since the last standup closed. The engine
 * folds it forward as events are emitted, so it is always current. Counts, not prose — S2 narrates these
 * same numbers.
 */
export interface DigestView {
  sinceRound: number;
  rounds: number;
  quiet: boolean;
  effects: number;
  fuses: number;
  events: number;
  decisions: number;
  decisionsTimedOut: number;
  concerns: number;
  concernsEscalated: number;
  workCompleted: number;
  promisesBroken: number;
  hires: number;
}

/** One backlog / WIP item (game-loop §2.8). */
export interface BacklogItem {
  id: string;
  type: string;
  status: string;
  est: number;
  progress: number;
  seat: string | null;
  /**
   * How much of its seat's capacity this item holds, and therefore how many effort units it advances
   * each round (DEC-17 M3). Always at least 1 — the rate was always here, it just could not be changed.
   * Optional on the type only so a payload recorded before M3 still parses.
   */
  rate?: number;
}

/** The planning gate: the objective + graded key results (game-loop §7.5). */
export interface OkrView {
  objective: string | null;
  keyResults: { id: string; description: string; grade: string }[];
}

/**
 * One open call in the decision queue (game-loop §6.5 / §7.4). The rights style says WHO may close it;
 * the counters say what is still missing before it can be closed (consults gathered, votes cast, sign-off).
 * Option prose is deferred to S2, so options are ids.
 */
export interface DecisionView {
  id: string;
  kind: string;
  style: string;
  owner: string;
  dueRound: number;
  deferrals: number;
  options: string[];
  worstCase: string;
  consulted: string[];
  requiredConsults: number;
  votes: number;
  quorum: number;
  approved: boolean;
  approver: string | null;
  /** True when the recipient owns this call. */
  mine: boolean;
}

/**
 * One row of the policy ledger (game-loop §7.6): a standing rule governance adopted. Repealed rows stay —
 * what the team tried and undid is part of the session's story.
 */
export interface PolicyView {
  id: string;
  status: string;
  adoptedRound: number;
  changedRound: number;
  amendment: string | null;
  cause: string;
  /** The authored amendment ids this policy can be moved onto. */
  amendments: string[];
}

/**
 * The director's pending event (game-loop §4.3): the fired card's id and its resolvable options. Narration
 * (the card/option prose) is deferred to S2, so options carry structural ids + a bet flag only. Null when idle.
 */
export interface ActiveEventView {
  id: string;
  /**
   * The director's own weight class for this card (`flavor` | `texture` | `consequence` | `scripted`,
   * game-loop §9.4). Null when the card is not in the pool. Clients scale how much CEREMONY a choice
   * carries to it (workspace-client.md §2b) rather than gating every choice the same way.
   */
  tier?: string | null;
  /** What the whole table is told happened. Null on an unauthored or untranslated card. */
  text: string | null;
  /**
   * What THIS seat can see and the others cannot (game-loop §4.3). The server looks it up for the
   * recipient's seat and nobody else's, so a client never holds another player's private detail -
   * there is nothing here to accidentally render.
   */
  reveal: string | null;
  options: {
    id: string;
    isBet: boolean;
    /** The authored label. Ids are for the log; a player picks by reading. */
    label: string | null;
    /** Visible rough odds, present only on a bet (§9.7). */
    odds: string | null;
  }[];
}

/** The recipient's own seat slice — present only when they occupy a seat (per-seat projection, §4.5). */
export interface YouSlice {
  seat: string;
  freeCapacity: number;
  checkIn: string;
}

/**
 * `stream.state.delta` payload: the per-seat-projected engine state (backend StateProjector). Ephemeral and
 * unsequenced — the latest wins; it is not part of the seq-ordered log.
 */
export interface GaugeView {
  telegraphedRisk: number;
  openConcerns: number;
  maxConcernHeat: number;
  wip: number;
  backlog: number;
  openReqs: number;
  openDecisions: number;
  activePolicies: number;
  /**
   * Fuses waiting on a CONDITION rather than a countdown (DEC-17 M2). Optional on the type only so a
   * payload recorded before M2 still parses.
   */
  armed?: ArmedFuseView[];
}

/**
 * One telegraphed fuse whose clock is a condition (DEC-17 M2).
 *
 * There is no countdown to render, and that is the point: "3 rounds" is exactly the number this fuse
 * does not have. What it has is a trigger, rendered through the ONE condition readout every other
 * condition uses - "detonates when quality < 45" is a thing a team can act on.
 *
 * Telegraphed only. A silent fuse stays silent (§9.6A) and the retro reveals the chain afterwards.
 */
export interface ArmedFuseView {
  metric: string;
  /** What armed it - the card or verb that scheduled it. */
  cause: string;
  condition: ConditionView | null;
}

/**
 * One comparison of a win/lose/advance condition, decomposed for display (backend `ConditionClause`).
 * The metric is always on the LEFT - the server re-orients an authored `80 <= stability` - so a renderer
 * never has to know which way round the author wrote it. Exactly one of `target`/`targetText` is set,
 * likewise `current`/`currentText`.
 */
export interface ConditionClauseView {
  metric: string;
  op: 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'neq';
  target: number | null;
  targetText: string | null;
  current: number | null;
  currentText: string | null;
  /** Whether this clause holds right now – the engine's own answer, not a client re-derivation. */
  met: boolean;
}

/** A whole condition: whether it holds, how its clauses combine, and each clause's standing. */
export interface ConditionView {
  met: boolean;
  join: 'single' | 'and' | 'or' | 'mixed';
  clauses: ConditionClauseView[];
}

/**
 * The goal (first-run-review.md §A1). Meters without targets are weather, not stakes. `win` and `lose`
 * are lists because composition appends the archetype's and product identity's own conditions: ANY win
 * entry wins, ANY lose entry loses. `advance` is what it takes to leave the current phase, or null on a
 * terminal phase / one with no authored gate.
 */
export interface GoalView {
  /**
   * WHICH GAME THIS IS (DEC-17 M4 §1.1). A `sim` has no win by construction, so a client must not read
   * an empty `win` as "nothing to play for" - that would put the player back exactly where §A1 found
   * them, with every meter as weather. With the frame on the board, the NEXT GATE is the goal and the
   * floors read as collapse rather than as losing. Optional so a delta recorded before M4 still parses.
   */
  frame?: 'excerpt' | 'sim';
  win: ConditionView[];
  lose: ConditionView[];
  advance: { toPhase: string; condition: ConditionView } | null;
}

/** One link of the milestone chain, as the board reads it (DEC-17 M1). */
export interface MilestoneLinkView {
  name: string;
  /** The deck's own diegetic name, per the reader's language, or null when unauthored. */
  title: string | null;
  reached: boolean;
  current: boolean;
  /** Decision ids, verbs and scene kinds this gate makes eligible. */
  unlocks: string[];
  /** The card the gate is guaranteed to fire, named but not described. */
  beat: string | null;
}

/**
 * THE MILESTONE (DEC-17 M1). The phase was always the milestone and the decks always said so: every
 * shipped deck has authored a localized `title` and a `goals` line per phase since it was written, and
 * neither ever reached a client - a player has only ever been shown the key. "renewal" is an identifier.
 *
 * The whole chain travels, not just the current link, because a gate nobody can see coming is a surprise
 * rather than a milestone: the workspace's calendar needs to say what is due next, and the terminal's
 * phase line needs somewhere to point.
 */
export interface MilestoneView {
  index: number;
  name: string | null;
  title: string | null;
  goals: string[];
  chain: MilestoneLinkView[];
  /** When each gate was reached - what turns a chain into a history. */
  crossed: { phase: string; round: number }[];
  /** The round the current span opened on: the gate retro's lower bound. */
  spanStart: number;
}

/**
 * The next scene the schedule owes, and how far away it is (`inRounds: 0` = this round, at day open).
 * Null when nothing is coming inside the lookahead horizon.
 *
 * A meeting nobody can see coming is an ambush rather than a calendar (workspace-client.md §2a). The
 * workspace renders it as a calendar strip; the terminal puts it on the board's phase line. It is a
 * prediction from current state — a team that runs its own standup tomorrow changes the answer — and it
 * never names a meeting the authored cadence does not own.
 */
export interface UpcomingSceneView {
  scene: string;
  inRounds: number;
}

/**
 * One relationship with the outside world, as the team PERCEIVES it (workspace-client.md §2a).
 *
 * `patienceBand` is banded and never raw: "patience 43" is the most mechanical number in the game, the
 * language law forbids it reaching a player (§2g), and §2o forbids charting a person's feelings at all.
 * The three bands are the escalation ladder's own states - quiet, insistent, paged.
 */
export interface StakeholderView {
  key: string;
  /** The person, or the organisation when the roster entry has no authored character. Null if neither. */
  name: string | null;
  agenda: string;
  patienceBand: 'steady' | 'impatient' | 'spent';
  /** How their wording hardens as patience drains (npc-voice §4.3); null when unauthored. */
  escalationStyle: string | null;
  brokenPromises: number;
}

/**
 * A promise the team has made and not yet resolved (outside-world §3; npc-llm-layer §7.5b).
 *
 * The engine has resolved kept and broken promises since S1 and no client could see one, so there was
 * nothing to name and no reason to make one - the dormant half of the loop §7.5b describes.
 */
export interface PromiseView {
  id: string;
  /** The outside-world key it is owed to - never a seat. */
  stakeholder: string;
  content: string;
  dueRound: number;
  /** Past its day and still open: what the next round tick will turn into a broken promise. */
  overdue: boolean;
}

/**
 * What this session is composed of (scenario-model §6): the pin identity, finally on the wire.
 *
 * Ids rather than prose, because what a client needs is something to key authored content and generated
 * assets off - the workspace's §2s brand takes its palette from the archetype and its logo from the
 * product identity - and an id cannot be mistaken for a sentence.
 */
export interface CompositionView {
  scenario: string;
  /** The composed game's name, in the reader's language. */
  name: string | null;
  archetype: string | null;
  culture: string | null;
  productIdentity: string | null;
}

export interface GameStatePayload {
  round: number;
  phase: string;
  deadline: number | null;
  started: boolean;
  outcome: string;
  meters: Record<string, number>;
  /**
   * What winning and losing mean here, with live progress. Optional on the type only so a payload
   * recorded before A1 still parses; the server always sends it.
   */
  goal?: GoalView | null;
  /**
   * Where the team is in the milestone chain (DEC-17 M1). Optional on the type for the same reason
   * `goal` is: a payload recorded before M1 still parses.
   */
  milestone?: MilestoneView | null;
  /** The situation digest — the "watch the fuse" gauges the standup surfaces (§6.5/§9.3). */
  gauges: GaugeView;
  seats: SeatView[];
  scene: SceneView | null;
  /** What the calendar owes next; null while a scene is running, a gate is open, or nothing is due. */
  upcoming?: UpcomingSceneView | null;
  /** The outside world: who is waiting on you, and how close to the edge they are (§2a). */
  stakeholders?: StakeholderView[];
  /**
   * Open promises this team owes the outside world (outside-world §3; npc-llm-layer §7.5b). Only the
   * OPEN ones: a resolved promise is a memory the stakeholder holds, not a live obligation.
   */
  promises?: PromiseView[];
  /** Scenario × archetype × culture × product identity — what company this is (§2s). */
  composition?: CompositionView | null;
  /** What has happened since the last standup closed (§6.5 beat 1). */
  digest: DigestView;
  backlog: BacklogItem[];
  okrs: OkrView;
  /** The open decision queue (§6.5): first-class calls with a clock and an owner. */
  decisions: DecisionView[];
  /** The policy ledger: the standing rules governance has adopted, amended or repealed (§7.6). */
  policies: PolicyView[];
  /** The director's pending event to resolve, or null when nothing is pending. */
  activeEvent: ActiveEventView | null;
  you: YouSlice | null;
}

// cmd.session.leave and cmd.session.sync carry no payload fields in S0.
export type LeavePayload = Record<string, never>;

// ---- evt.* committed events ---------------------------------------------------------------------

/** `evt.chat.message` payload (backend ChatSendCommand). */
export interface ChatMessageEvent {
  playerId: string;
  text: string;
  /**
   * The room this was typed in (register item 16). ABSENT means `#team` — that default is the
   * client's convention, not the server's: the server validates the token's shape and has no idea
   * what a room is, because the rooms are the skin's vocabulary (the semantic-projection rule).
   *
   * Absent on every event committed before item 16, which is what makes the whole change additive:
   * a replayed session renders exactly as it did.
   */
  channel?: string;
}

/**
 * `evt.game.action` payload: a committed gameplay input plus the engine events it emitted. The input is
 * the replay record; the emitted list is what the action feed renders ("who did what, and what came of it").
 */
export interface GameActionEvent {
  input: GameActionPayload;
  emitted: { kind: string; seat: string | null; note: string | null }[];
}

/**
 * `evt.game.narration` payload (s2-llm-layer §4): prose for a beat that was already committed. It arrives
 * AFTER the action it describes - narration runs in a background job, so a slow model delays words and
 * never a turn - which is why it carries `forSeq` rather than assuming it is the latest thing.
 *
 * `source` is deliberately on the wire: when the model is unavailable the line is the engine's own notes,
 * and the UI says so rather than passing scripted text off as authored prose (meeting-llm §7).
 */
/**
 * `evt.game.fidelityChanged` payload (S2.6). Carries the SPEND as well as the rung, because "coaching
 * stopped" without "you have used 82% of this session's budget" is news a player can do nothing with.
 */
export interface FidelityChangedEvent {
  from: string;
  to: string;
  spentMicro: number;
  ceilingMicro: number;
  percentUsed: number;
  /** The purposes this rung turns off. Empty on the way back up, which is itself the news. */
  disabled: string[];
}

/**
 * `evt.npc.said` payload (S2.3). An NPC's line about a beat that is already committed - the same shape
 * narration uses, because it is the same kind of thing: words about a decision, never the decision.
 */
export interface NpcSaidEvent {
  forSeq: number;
  seat: string;
  text: string;
  source: 'llm' | 'scripted';
  /**
   * The room to say it IN (npc-llm-layer §3.5 tier 1): a public question gets a public answer, in the
   * room it was asked in. Absent on the scheduled voices (banter, stance, the beat's own lines), which
   * belong to the beat they describe rather than to a room somebody typed in.
   */
  channel?: string;
}

export interface GameNarrationEvent {
  forSeq: number;
  text: string;
  source: 'llm' | 'scripted';
  /**
   * The server's beat-split (workspace-client.md §2j): the committed line, already short, as the posts a
   * messenger shows. A client renders these rather than splitting `text` itself - the law's mechanism
   * clause is that the committed text is short and no client re-splits. `text` remains the joined form.
   */
  posts?: string[];
  /**
   * Who posted it (§2n): `monitoring`, `deploy`, `digest` or `meeting`. Decided server-side from the
   * beat's own engine events, because a messenger has no narrator - a voice that sees everything and
   * belongs to nobody is a surveillance camera.
   */
  voice?: string;
}

/**
 * `evt.scene.narrated` payload (S2.4; meeting-llm-layer.md §3): a scene beat read out loud. Like narration
 * it arrives after the beat it describes and carries `forSeq` to say which one.
 *
 * There is no scripted tier here. When the model is unavailable this event simply does not arrive, and the
 * templated digest the board already renders is what the team reads - so `source` is on the wire for the
 * badge, not because a scripted line is expected.
 */
/**
 * `evt.milestone.reached` payload (DEC-17 M1): the gate, and the engine's reflection over the span behind
 * it. The document is the same `{ package, text, source }` shape a retro travels in wherever it is read -
 * one shape, one render path - and the package carries `gate` and `spanFrom`, which are what tell a client
 * this is a CHAPTER and not an ending. The all-hands takeover belongs to the finale and only to it.
 */
export interface MilestoneReachedEvent {
  package: MilestoneSpanPackage;
  text?: string | null;
  source?: string | null;
}

/** The span package a crossing carries: the deterministic floor every client can render unaided. */
export interface MilestoneSpanPackage {
  /** The gate reached. Present iff this is a mid-session chapter; absent on the end-of-session retro. */
  gate?: string | null;
  /** The round the span opened after. */
  spanFrom?: number;
  rounds: number;
  headline: string;
  meters?: Record<string, number>;
  metricArcs?: Record<string, number[]>;
  concerns?: { id: string; topic: string; status: string; escalated: boolean }[];
  decisions?: { id: string; chose: string; owner: string; round: number }[];
}

/**
 * `evt.milestone.narrated` payload (DEC-17 M1): prose for a crossing that is already committed. `forSeq`
 * binds it to its crossing, so a client holding both renders one document and a client that reconnected
 * between them renders the package alone and gains the words when they replay.
 */
export interface MilestoneNarratedEvent {
  gate: string;
  forSeq: number;
  text: string;
  source: 'llm' | 'scripted';
  /** The server's beat-split (§2j), same contract as scene narration. */
  posts?: string[];
  /** The meeting-notes bot (§2n). A milestone write-up is exactly what that bot is for. */
  voice?: string;
}

export interface SceneNarratedEvent {
  scene: string;
  beat: string;
  forSeq: number;
  text: string;
  source: 'llm' | 'scripted';
  /**
   * The server's beat-split (workspace-client.md §2j): the committed line, already short, as the posts a
   * messenger shows. A client renders these rather than splitting `text` itself - the law's mechanism
   * clause is that the committed text is short and no client re-splits. `text` remains the joined form.
   */
  posts?: string[];
  /**
   * Who posted it (§2n): `monitoring`, `deploy`, `digest` or `meeting`. Decided server-side from the
   * beat's own engine events, because a messenger has no narrator - a voice that sees everything and
   * belongs to nobody is a surveillance camera.
   */
  voice?: string;
}

/** One per-round effect on a previewed policy card. */
export interface PolicyEffectView {
  metric: string;
  value: number;
}

/** A compiled policy card as the table reads it (S2.4 §4.2). */
export interface PolicyCardView {
  id: string;
  name: string;
  prose: string;
  effects: PolicyEffectView[];
  downsideEffects: PolicyEffectView[];
  processTaxRounds: number;
}

/**
 * `evt.policy.previewed` payload: a validated card the table can read before anyone adopts it. It
 * changes no engine state - adoption is the separate `proposeCompiledPolicy` input - so this is an
 * event about a proposal, not the proposal taking effect.
 */
export interface PolicyPreviewedEvent {
  card: PolicyCardView;
  /** `llm` when a model compiled it, `scripted` when the call degraded. */
  source: 'llm' | 'scripted';
}

/**
 * `evt.presence.changed` payload (protocol.md §9, backend RealtimeEnvelopes.Presence).
 * NOTE: presence is broadcast UNSEQUENCED — the envelope carries NO `seq`. The store treats it as a
 * live side-channel, not part of the seq-ordered log.
 */
export interface PresenceChangedEvent {
  player: string;
  state: PresenceState;
  lastSeen: string;
}

/** `evt.session.created` payload. */
export interface SessionCreatedEvent {
  name: string;
  visibility: string;
  ownerId: string;
}

/** `evt.session.memberJoined` payload. */
export interface MemberJoinedEvent {
  playerId: string;
  roleKey: string | null;
  /** What this player calls themselves; empty when they never gave a name. */
  displayName?: string;
}

/** `evt.session.memberLeft` payload. */
export interface MemberLeftEvent {
  playerId: string;
  reason?: string;
}
